import * as Sentry from "@sentry/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { DateTime } from "luxon";
import { enqueueSnackbar } from "notistack";

import { QUERY_KEYS, useGetTransportAxiosInstance } from "@/constants";
import { decodeArrayBufferToString } from "@/helpers/strings";
import useAuth from "@/providers/hooks/useAuth";
import {
    HotelClinicManagerTasksByDateResponseSchema,
    HotelClinicManagerTasksByDateResponseSchemaType,
} from "@/schemas/hotelClinicSchema";
import { CommonExportReq } from "@/types/export.types";
import { HotelPickupReasons } from "@/types/hotel.types";

export const emptyHotelClinicManagerTasksByDate: HotelClinicManagerTasksByDateResponseSchemaType = {
    tasks: [],
    unassignedPassengers: [],
};

/**
 * Parse transport-svc hotel-clinic DTO. On zod failure: Sentry + safe empty (no throw).
 */
export function parseHotelClinicManagerTasksByDateResponse(
    raw: unknown
): HotelClinicManagerTasksByDateResponseSchemaType {
    const result = HotelClinicManagerTasksByDateResponseSchema.safeParse(raw);
    if (result.success) return result.data;

    Sentry.captureException(result.error, {
        extra: {
            issues: result.error.issues,
            zodFormErrors: result.error.format(),
        },
    });
    console.error(result.error);
    return raw as HotelClinicManagerTasksByDateResponseSchemaType;
}

export const getHotelClinicManagerTasksByDateQueryKey = (isoDate: string) =>
    [QUERY_KEYS.GET_HOTEL_CLINIC_TASKS_BY_DATE, isoDate] as const;

export const useGetHotelClinicManagerTasksByDate = ({
    date,
    enabled = true,
}: {
    date: DateTime;
    enabled?: boolean;
}) => {
    const { auth } = useAuth();
    const axiosInstance = useGetTransportAxiosInstance();
    const isoDate = date.toISODate();

    return useQuery<HotelClinicManagerTasksByDateResponseSchemaType>({
        queryKey: getHotelClinicManagerTasksByDateQueryKey(isoDate!),
        enabled: enabled && !!auth?.accessToken && !!isoDate,
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 2,
        queryFn: async () => {
            const { data } = await axiosInstance.get<unknown>(`/hotel-clinic/tasks/${isoDate}`);
            return parseHotelClinicManagerTasksByDateResponse(data);
        },
    });
};

export type ClinicUnassignedPassengersExportPayload = CommonExportReq & {
    statusesTexts: { [key in HotelPickupReasons]: string };
    /** Same meaning as Hotel Planning export `cancelled`: filter excludes cancelled passengers from view/export when true. */
    cancelled: boolean;
};

/** POST `/passengers/:date/export` — same payload shape as hotel planning export minus filter fields */
export function useExportClinicUnassignedPassengersMutation() {
    const axiosInstance = useGetTransportAxiosInstance();

    return useMutation<ArrayBuffer, Error, { date: DateTime } & ClinicUnassignedPassengersExportPayload>({
        mutationFn: async ({ date, ...payload }) => {
            const isoDate = date.toISODate();
            if (!isoDate) throw new Error("Invalid date");

            return axiosInstance
                .post<ArrayBuffer>(`/hotel-clinic/passengers/${isoDate}/export`, payload, {
                    responseType: "arraybuffer",
                })
                .then((res) => res.data)
                .catch((err) => {
                    Sentry.captureException(err);
                    const errorBuffer = (err as AxiosError<ArrayBuffer>)?.response?.data as ArrayBuffer | undefined;
                    if (errorBuffer) {
                        const errorMessage = decodeArrayBufferToString(errorBuffer);
                        enqueueSnackbar(errorMessage, { variant: "error" });
                    }
                    throw err;
                });
        },
    });
}
